import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'react-apollo';
import { WrappedComponentProps, injectIntl } from 'react-intl';
import { get } from 'lodash';

import BaseInputText from '../InputText';
import BaseInputColor from '../InputColor';

import { IDispatch } from '../../../../../modules/stores';

import { ISpace, HorizontalAlignment, VerticalAlignment } from '../../../../../types/space';
import BaseInputDropdown from '../InputDropdown';
import { ElementTypes } from '../../../../../types/element';
import BaseInputRadio from '../InputRadio';
import { FONT_WEIGHT_OPTIONS, FONT_FAMILY } from '../../../../../constants/space';

type IConnectProps = ReturnType<typeof mapDispatch>;
interface IProps extends IConnectProps, WrappedComponentProps {
  space: ISpace<ElementTypes.StaticText>;
  editKey?: 'headingStyle' | 'rowStyle';
  disableVerticalAlign?: boolean;
  disableHorzontalAlign?: boolean;
}

const FontConfigs = ({
  space,
  disableVerticalAlign = false,
  disableHorzontalAlign = false,
  updateSpaceConfig,
  editKey,
  intl: { formatMessage },
}: IProps) => {
  const path = `${editKey ? `${editKey}` : 'config'}`;

  const FONT_HORZ_ALIGN_OPTIONS = [
    { name: formatMessage({ id: 'sidebar.align.left' }), value: HorizontalAlignment.Left },
    { name: formatMessage({ id: 'sidebar.align.center' }), value: HorizontalAlignment.Center },
    { name: formatMessage({ id: 'sidebar.align.right' }), value: HorizontalAlignment.Right },
  ];

  const FONT_VERT_ALIGN_OPTIONS = [
    { name: formatMessage({ id: 'sidebar.align.top' }), value: VerticalAlignment.Top },
    { name: formatMessage({ id: 'sidebar.align.middle' }), value: VerticalAlignment.Center },
    { name: formatMessage({ id: 'sidebar.align.bottom' }), value: VerticalAlignment.Bottom },
  ];

  return (
    <>
      <BaseInputDropdown
        name="font"
        setFn={(val: any) =>
          updateSpaceConfig({
            id: space.id,
            propKey: editKey,
            updates: { font: val },
          })
        }
        options={FONT_FAMILY}
        title={formatMessage({ id: 'sidebar.font' })}
        value={get(space, `${path}.font`)}
        includeEmptyOption={false}
      />
      <BaseInputText
        name="fontSize"
        setFn={(val: number) => {
          updateSpaceConfig({
            id: space.id,
            propKey: editKey,
            updates: { fontSize: Number(val) },
          });
        }}
        title={formatMessage({ id: 'sidebar.fontSize' })}
        type="number"
        value={get(space, `${path}.fontSize`)}
      />
      <BaseInputDropdown
        name="weight"
        setFn={(val: any) =>
          updateSpaceConfig({
            id: space.id,
            propKey: editKey,
            updates: { fontWeight: val },
          })
        }
        options={FONT_WEIGHT_OPTIONS[get(space, `${path}.font`)]}
        title={formatMessage({ id: 'sidebar.fontWeight' })}
        value={get(space, `${path}.fontWeight`)}
        includeEmptyOption={false}
      />
      <BaseInputText
        name="lineHeight"
        setFn={(val: number) => {
          updateSpaceConfig({
            id: space.id,
            propKey: editKey,
            updates: { lineHeight: String(val) },
          });
        }}
        title={formatMessage({ id: 'sidebar.lineHeight' })}
        type="number"
        step="0.1"
        value={get(space, `${path}.lineHeight`)}
      />
      {!disableHorzontalAlign && (
        <BaseInputRadio
          name="horizontalAlign"
          setFn={(val: HorizontalAlignment) => {
            updateSpaceConfig({
              id: space.id,
              propKey: editKey,
              updates: { horizontalAlign: val },
            });
          }}
          options={FONT_HORZ_ALIGN_OPTIONS}
          title={formatMessage({ id: 'sidebar.align.horizontal' })}
          value={get(space, `${path}.horizontalAlign`)}
        />
      )}
      {!disableVerticalAlign && (
        <BaseInputRadio
          name="verticalAlign"
          setFn={(val: VerticalAlignment) => {
            updateSpaceConfig({
              id: space.id,
              propKey: editKey,
              updates: { verticalAlign: val },
            });
          }}
          options={FONT_VERT_ALIGN_OPTIONS}
          title={formatMessage({ id: 'sidebar.align.vertical' })}
          value={get(space, `${path}.verticalAlign`)}
        />
      )}
      <BaseInputColor
        name="color"
        setFn={(val: string) => {
          updateSpaceConfig({
            id: space.id,
            propKey: editKey,
            updates: { textColor: val },
          });
        }}
        title={formatMessage({ id: 'sidebar.textColor' })}
        type="text"
        value={get(space, `${path}.textColor` || '')}
      />
    </>
  );
};

const mapDispatch = ({ space }: IDispatch) => ({
  updateSpaceConfig: (payload: any) => space.updateSpaceConfig(payload),
});

export default compose(
  injectIntl,
  connect(
    () => ({}),
    // @ts-ignore
    mapDispatch,
  ),
)(FontConfigs);
